import { useRef } from "react";
import { Reveal } from "@/components/Reveal";
import { CompassKeyHover } from "@/components/CompassKeyHover";
import { SectionParticles } from "@/components/SectionParticles";
import { SectionBg } from "@/components/SectionBg";
import { CAMPAIGN } from "@/lib/campaign";
import { scrollToId } from "@/lib/scroll";

const CREDENTIALS = [
  { label: "COMPANY", value: "Kinsfolk Mortgage" },
  { label: "INDIVIDUAL NMLS", value: "#1703351" },
  { label: "COMPANY NMLS", value: "#2610890" },
  { label: "LICENSED IN", value: "Florida" },
];

/* SECTION 8 — HOST */
export const Host = () => {
  const portraitRef = useRef(null);

  return (
    <section id="host" className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="host-section">
      <SectionBg img="bg-home-4.jpg" strength={0.93} />
      <SectionParticles variant="houses" />
      <div className="relative z-10 max-w-6xl mx-auto text-left">
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-12">
          [005] — YOUR HOST
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <div
              ref={portraitRef}
              className="relative rounded-[8px] overflow-hidden border border-[var(--line)] portrait-pulse md:animate-none"
              data-testid="host-portrait-panel"
            >
              <CompassKeyHover hostRef={portraitRef} />
              <img
                src={CAMPAIGN.headshot}
                alt={`${CAMPAIGN.hostName} — ${CAMPAIGN.hostCompany}`}
                className="relative z-10 w-full h-auto object-cover mix-blend-normal"
                loading="lazy"
                style={{ aspectRatio: "1 / 1" }}
              />
              <div className="relative z-10 glass-panel px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">{CAMPAIGN.hostName}</p>
                  <p className="font-mono text-[10px] tracking-[0.12em] text-[var(--muted)]">
                    {CAMPAIGN.hostCompany}
                  </p>
                </div>
                <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--accent)]">
                  NMLS #{CAMPAIGN.hostNmls}
                </span>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <h2 className="font-display font-semibold text-3xl md:text-4xl leading-tight text-white mb-8">
                A CLEAR EXPLANATION BEFORE AN INDIVIDUAL APPLICATION.
              </h2>
            </Reveal>
            <Reveal>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] mb-10">
                <p>
                  Chaunci Witherspoon, MBA is a Florida mortgage professional with Kinsfolk Mortgage. Her work
                  starts from a simple observation: most people exploring a VA home loan are not short on
                  motivation—they are short on organized information, and the questions they carry deserve
                  better than one-line answers.
                </p>
                <p>
                  VA home financing is a primary focus of her mortgage work. Session after session, she has
                  watched the same pattern repeat: a buyer hears "zero down," then quietly wrestles alone with
                  eligibility, entitlement, cash categories, credit, and the offer itself. She built this
                  workshop to bring those questions into one room, in one sequence, in plain language.
                </p>
                <p>
                  That sequence is the Benefit-to-Keys Blueprint™: Verify → Model → Move. Verify the questions
                  that must be confirmed, model the inputs that shape an individualized review, and then move—on
                  purpose—toward whichever next step fits, before any individual application begins.
                </p>
              </div>
            </Reveal>
            <Reveal>
              <div className="grid grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)] rounded-[6px] overflow-hidden mb-10">
                {CREDENTIALS.map((c) => (
                  <div key={c.label} className="bg-[var(--background)] p-5">
                    <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--muted)] mb-1">{c.label}</p>
                    <p className="text-sm md:text-base font-medium text-white">{c.value}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <button
              data-testid="host-cta-button"
              onClick={() => scrollToId("register")}
              className="glint bg-[var(--accent)] text-[var(--background-deep)] font-semibold text-sm tracking-[0.12em] px-8 py-4 rounded-[4px] hover:brightness-110 hover:-translate-y-[1px] transition-[transform,filter] duration-200"
            >
              RESERVE MY FREE SEAT
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
